import React, { lazy, Suspense } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import axios from 'axios';

// Root Route - serves as the global error boundary and layout wrapper if needed
// Root Component
function RootComponent() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kijeka-blue"></div></div>}>
      <Outlet />
    </Suspense>
  );
}

// Root Route - serves as the global error boundary and layout wrapper
const rootRoute = createRootRoute({
  component: RootComponent,
  errorComponent: ({ error }) => {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <h1 className="text-2xl font-bold text-kijeka-blue">Something went wrong</h1>
        <p className="text-gray-600">{error.message}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-kijeka-blue text-white rounded hover:bg-opacity-90 transition-all"
        >
          Reload Page
        </button>
      </div>
    );
  },
  // Root loader removed to prevent blocking initial paint. 
  // Data is now fetched non-blockingly in the Navbar component.
});

// Layouts
const HomeLayout = lazy(() => import('./Outlet/home'));
const AdLayout = lazy(() => import('./Outlet/Ad'));

// Pages
const Homepage = lazy(() => import('./Pages/Homepage'));
const Careers = lazy(() => import('./Pages/Careers'));
const ContactUs = lazy(() => import('./Pages/ContactUs'));
const Blog = lazy(() => import('./Components/Blogs/Blog'));
const BlogNew = lazy(() => import('./Components/BlogsNew/BlogNew'));
const Details = lazy(() => import('./Components/ProductDetails/Details'));
const About = lazy(() => import('./Components/Aboutus/About'));
const Privacy = lazy(() => import('./Components/Privacy/Privacy'));
const TermsCon = lazy(() => import('./Components/TearmsCondition/TermsCon'));
const Inquiry = lazy(() => import('./Components/Inquiry/Inquiry'));
const Page404 = lazy(() => import('./Pages/404/Page404'));
const InquiryForm = lazy(() => import('./Pages/Form'));
const AdPage = lazy(() => import('./Ad/Components/HomePage/Adpage'));

// --- Home Routes Group ---
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'home-layout',
  component: HomeLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/',
  component: Homepage,
});

const careersRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/careers',
  component: Careers,
});

const aboutRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/about',
  component: About,
});

const blogLinkRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/blog/$link',
  component: Blog,
  loader: async ({ params }) => {
    try {
      const server = (process.env.REACT_APP_SERVER || "").replace(/\/$/, "");
      const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
      const apiServer = (isHttps && server.startsWith("http://")) ? "" : server;
      const res = await axios.get(apiServer + "/api/blogdata");
      const blogs = Array.isArray(res.data?.data) ? res.data.data : [];
      
      const normalizedParam = params.link?.replace(/\/$/, '').toLowerCase();
      let selectedBlog = blogs.find(b => b.blogLink?.replace(/\/$/, '').toLowerCase() === normalizedParam);
      
      // Fallback: match by title (convert slug back to spaces)
      if (!selectedBlog) {
        const titleGuess = normalizedParam?.replace(/-/g, ' ');
        selectedBlog = blogs.find(b => b.title?.toLowerCase() === titleGuess);
      }
      
      selectedBlog = selectedBlog || blogs[0] || null;
      return { blogs, selectedBlog };
    } catch (error) {
      console.error("Error loading blog data:", error);
      return { blogs: [], selectedBlog: null };
    }
  }
});

const blogRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/blog',
  component: Blog,
  loader: async () => {
    try {
      const server = (process.env.REACT_APP_SERVER || "").replace(/\/$/, "");
      const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
      const apiServer = (isHttps && server.startsWith("http://")) ? "" : server;
      const res = await axios.get(apiServer + "/api/blogdata");
      const blogs = Array.isArray(res.data?.data) ? res.data.data : [];
      return { blogs, selectedBlog: blogs[0] || null };
    } catch (error) {
      console.error("Error loading blog data:", error);
      return { blogs: [], selectedBlog: null };
    }
  }
});


const contactRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/contact',
  component: ContactUs,
});

const joinDetailRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/career-details/$link',
  component: lazy(() => import('./Components/JoinDetail/JoinDetail')),
});

const jobAppRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/job-apply/$link',
  component: lazy(() => import('./Components/JobApplicationForm/JobAppForm')),
});

const ourProductsRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/our-products',
  component: BlogNew,
  loader: async () => {
    const res = await axios.get(
      (process.env.REACT_APP_SERVER || "").replace(/\/$/, "") + "/api/getCategoryProducts/?category=Hot Products&subCategory="
    );
    return { productsData: res.data };
  }
});

const dynamicSubLinkRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/$link/$subLink',
  component: BlogNew,
  loader: async ({ params }) => {
    const res = await axios.get(
      (process.env.REACT_APP_SERVER || "").replace(/\/$/, "") + "/api/getCategoryProducts/?category=" + params.link + "&subCategory=" + params.subLink
    );
    return { productsData: res.data };
  }
});

const dynamicLinkRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/$link',
  component: BlogNew,
  loader: async ({ params }) => {
    const res = await axios.get(
      (process.env.REACT_APP_SERVER || "").replace(/\/$/, "") + "/api/getCategoryProducts/?category=" + params.link + "&subCategory="
    );
    return { productsData: res.data };
  }
});

const productDetailRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/product/$link',
  component: Details,
  loader: async ({ params }) => {
    const res = await axios.get(
      (process.env.REACT_APP_SERVER || "").replace(/\/$/, "") + "/api/getProductDetail/?productName=" + params.link
    );
    return { productDetail: res.data.data };
  }
});

const privacyRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/privacy-policy',
  component: Privacy,
});

const termsRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/terms-and-condition',
  component: TermsCon,
});

const inquiryRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/add-to-inquiry',
  component: Inquiry,
});

const page404Route = createRoute({
  getParentRoute: () => homeRoute,
  path: '/page-not-found',
  component: Page404,
});

const inquiryFormRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/inquiry-form',
  component: InquiryForm,
});

// --- Ad Routes Group ---
const adLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ad',
  component: AdLayout,
});

const adPageRoute = createRoute({
  getParentRoute: () => adLayoutRoute,
  path: '/',
  component: AdPage,
});

// Create the Route Tree
const routeTree = rootRoute.addChildren([
  homeRoute.addChildren([
    indexRoute,
    careersRoute,
    aboutRoute,
    blogLinkRoute,
    blogRoute,
    contactRoute,
    joinDetailRoute,
    jobAppRoute,
    ourProductsRoute,
    privacyRoute,
    termsRoute,
    inquiryRoute,
    page404Route,
    inquiryFormRoute,
    productDetailRoute,
    dynamicSubLinkRoute,
    dynamicLinkRoute,
  ]),
  adLayoutRoute.addChildren([
    adPageRoute,
  ]),
]);

// Initialize the Router with performance-focused defaults
export const router = createRouter({ 
  routeTree,
  defaultStaleTime: 1000 * 60 * 5, // 5 minutes cache
  defaultGcTime: 1000 * 60 * 10,  // 10 minutes garbage collection
});

export default router;
